import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Minus } from "lucide-react";
import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";

export default function CartPage() {
  const { cart, removeItem, updateQty } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shabbatHold, setShabbatHold] = useState(false);

  const goCheckout = async () => {
    if (!user) { navigate("/login?redirect=/cart"); return; }
    try {
      const { data } = await client.post("/payments/checkout", { origin_url: window.location.origin, shabbat_hold: shabbatHold });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start checkout."); }
  };

  return (
    <div className="container-editorial mt-8 md:mt-14">
      <div className="border-b border-[#EAE5D9] pb-10 mb-12">
        <p className="label-uppercase text-[#B24C38]">Your Bag</p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter mt-3">Checkout</h1>
      </div>

      {cart.items.length === 0 ? (
        <div className="py-24 text-center">
          <p className="font-serif text-3xl mb-6">Your bag is empty.</p>
          <Link to="/shop" className="btn-terracotta">Explore the Shop →</Link>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-8 md:gap-16">
          <div className="col-span-12 md:col-span-8 space-y-8">
            {cart.items.map((item, idx) => {
              const p = item.product; if (!p) return null;
              return (
                <div key={idx} className="flex gap-6 border-b border-[#EAE5D9] pb-8" data-testid={`cartpage-item-${idx}`}>
                  <div className="w-32 md:w-40 aspect-[3/4] bg-[#EAE5D9] overflow-hidden shrink-0">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover"/>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between gap-4">
                      <div>
                        <Link to={`/product/${p.id}`} className="font-serif text-xl md:text-2xl leading-tight link-underline">{p.name}</Link>
                        <p className="text-sm text-[#4A4A4A] mt-2">
                          {item.size && `Size ${item.size}`}{item.color && ` · ${item.color}`}
                        </p>
                      </div>
                      <button onClick={() => removeItem(idx)} data-testid={`cartpage-remove-${idx}`}><X className="w-4 h-4"/></button>
                    </div>
                    <div className="flex justify-between items-center mt-6">
                      <div className="flex items-center border border-[#111]">
                        <button onClick={() => updateQty(idx, item.quantity-1)} className="p-2"><Minus className="w-3 h-3"/></button>
                        <span className="px-4 text-sm">{item.quantity}</span>
                        <button onClick={() => updateQty(idx, item.quantity+1)} className="p-2"><Plus className="w-3 h-3"/></button>
                      </div>
                      <p className="font-serif text-2xl">${(p.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="col-span-12 md:col-span-4">
            <div className="bg-[#EAE5D9] p-8">
              <h2 className="font-serif text-3xl tracking-tighter">Summary</h2>
              <div className="mt-8 space-y-4">
                <div className="flex justify-between"><span>Subtotal</span><span data-testid="cartpage-subtotal">${cart.total?.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm text-[#4A4A4A]"><span>Shipping</span><span>Calculated at checkout</span></div>
              </div>

              <label className="mt-8 pt-6 border-t border-[#c9c2b0] flex gap-3 items-start cursor-pointer" data-testid="shabbat-hold-toggle">
                <input type="checkbox" checked={shabbatHold} onChange={e => setShabbatHold(e.target.checked)}
                       className="mt-1 w-4 h-4 accent-[#B24C38]"/>
                <span>
                  <span className="label-uppercase text-[#B24C38] block">Shabbat delivery hold</span>
                  <span className="text-sm text-[#4A4A4A] block mt-1">
                    Pause packing & shipping over Shabbos and Yom Tov. Your ateliers will ship the next business day.
                  </span>
                </span>
              </label>

              <button onClick={goCheckout} className="btn-terracotta w-full justify-center mt-6" data-testid="cartpage-checkout-btn">
                Checkout with Stripe →
              </button>
              <p className="text-xs text-[#4A4A4A] mt-4 text-center">Secure payment · Test card: 4242 4242 4242 4242</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
